/**
 * StudioReportSections — the compact accordion form the Report Studio's
 * Details inspector renders (the report twin of StudioConfigSections). Binds
 * straight to ReportConfigContext, so it IS the same form state as the classic
 * page — sections carry completeness badges (the same "what's missing" signal
 * the copilot receives as pageContext) and fields flash teal for ~2.4s after
 * update_page_config patched them.
 */
import { useState } from 'react';
import { FiChevronDown } from 'react-icons/fi';
import { useReportConfig } from '../../context/ReportConfigContext';
import { Select, inputSmClass } from '../common/FormControls';
import { applicabilityMatrix, caroApplicable, determineCaroApplicability } from '../../data/reports/auditorReport/companyTypes';
import { CARO_CLAUSE_IDS, caroClauseHeading } from '../../data/reports/auditorReport/caro';

// Radius scale is deliberate: overlay frame 16 (--r-xl) → section cards 12
// (rounded-xl) → controls 8 (rounded-lg). No off-scale values.
const inputCls = inputSmClass; // design-system compact input, not a raw element
const labelCls = 'block text-[9.5px] uppercase tracking-[0.5px] font-semibold text-[#9a9080] mb-1';

// A field flashes teal after the copilot patched it ("AI touched this").
// Inline styles ONLY while flashing — a permanent inline border/shadow would
// override the shared control's own hover/focus chrome.
const borderFor = (flashKeys, key) => (
  flashKeys && flashKeys.has(key)
    ? {
      borderColor: '#145f5f',
      boxShadow: '0 0 0 2px rgba(20,95,95,0.25)',
      transition: 'box-shadow 0.4s ease, border-color 0.4s ease',
    }
    : undefined
);

// The shared <Select> owns its own chrome, so the flash rides on a wrapper
// (a ring around the control) rather than on the control's own border.
const flashRing = (flashKeys, key) => ({
  borderRadius: 8,
  boxShadow: flashKeys && flashKeys.has(key) ? '0 0 0 2px rgba(20,95,95,0.35)' : 'none',
  transition: 'box-shadow 0.4s ease',
});

const APPLIC_PILL = {
  yes:  { background: '#e8efe5', color: '#4a6b3f' },
  must: { background: '#e3f0ed', color: '#145f5f' },
  cond: { background: '#f3ebd7', color: '#8a6a1f' },
  no:   { background: '#f2eee6', color: '#8a7d66' },
};

function Section({ title, missing, defaultOpen = true, children }) {
  const [open, setOpen] = useState(defaultOpen);
  return (
    <div className="rounded-xl border bg-white overflow-hidden" style={{ borderColor: '#e2d9c8' }}>
      <button
        type="button"
        onClick={() => setOpen((o) => !o)}
        className="w-full flex items-center justify-between px-3 py-2 text-left"
      >
        <span className="text-[11px] font-bold uppercase tracking-[0.5px] text-[#08111f]">{title}</span>
        <span className="flex items-center gap-2">
          {missing > 0 && (
            <span className="text-[9.5px] font-semibold px-1.5 py-[2px] rounded-full" style={{ background: 'rgba(176,137,58,0.12)', color: '#8a6a1f' }}>
              {missing} missing
            </span>
          )}
          <FiChevronDown size={14} className={`text-[#9a9080] transition-transform ${open ? 'rotate-180' : ''}`} />
        </span>
      </button>
      {open && <div className="px-3 pb-3 space-y-2">{children}</div>}
    </div>
  );
}

/**
 * Annexure A clause comments the auditor has restated — the MANUAL half of the
 * same channel Vee writes to (`config.caroClauses`). Only overridden clauses
 * are listed: the 44 template comments stay in the document, where they are
 * read in context, and a 44-field form would bury the handful that actually
 * carry a finding. Adding a new override is done by asking Vee (or by editing
 * the clause on the annexure tab); editing or reverting one is done here, so a
 * clause the AI restated is never stuck behind the chat.
 */
function CaroClauseOverrides({ config, updateConfig }) {
  const overrides = config.caroClauses && typeof config.caroClauses === 'object' ? config.caroClauses : {};
  // Document order, not object-key order — the inspector should read like the
  // annexure it mirrors.
  const ids = CARO_CLAUSE_IDS.filter((id) => String(overrides[id] || '').trim());
  if (!caroApplicable(config) || !ids.length) return null;

  const setClause = (id, value) => {
    const next = { ...overrides };
    if (String(value).trim()) next[id] = value; else delete next[id];
    updateConfig('caroClauses', next);
  };

  return (
    <div className="rounded-xl border bg-white overflow-hidden" style={{ borderColor: '#e2d9c8' }}>
      <div className="px-3 py-2">
        <span className="text-[11px] font-bold uppercase tracking-[0.5px] text-[#08111f]">Annexure A — restated clauses</span>
        <div className="text-[10px] leading-relaxed mt-[2px]" style={{ color: '#7a7060' }}>
          These CARO clauses print your wording instead of the standard comment.
        </div>
      </div>
      <div className="px-3 pb-3 space-y-2.5">
        {ids.map((id) => (
          <div key={id}>
            <div className="flex items-baseline justify-between gap-2">
              <label className={labelCls} title={caroClauseHeading(id)}>{`Clause ${id}`}</label>
              <button
                type="button"
                onClick={() => setClause(id, '')}
                className="text-[10px] underline decoration-dotted underline-offset-2 hover:decoration-solid mb-1"
                style={{ color: '#145f5f' }}
                title="Print the standard CARO wording for this clause again"
              >
                Revert
              </button>
            </div>
            {/* No flash ring here: unlike a form field, the whole content of a
                restated clause changes, so the change is self-evident. */}
            <textarea
              className={`${inputCls} min-h-[76px] leading-relaxed`}
              value={overrides[id]}
              onChange={(e) => setClause(id, e.target.value)}
            />
          </div>
        ))}
      </div>
    </div>
  );
}

export default function StudioReportSections({ flashKeys }) {
  const { config, report, updateConfig } = useReportConfig();
  const matrix = applicabilityMatrix(config.companyType);
  const caro = determineCaroApplicability(config);

  const field = (key, label, placeholder = '') => (
    <div>
      <label className={labelCls}>{label}</label>
      <input
        className={inputCls}
        style={borderFor(flashKeys, key)}
        value={config[key] || ''}
        placeholder={placeholder}
        onChange={(e) => updateConfig(key, e.target.value)}
      />
    </div>
  );

  const toggle = (key, label, disabled = false) => (
    <label
      className="flex items-center justify-between gap-2 rounded-lg border px-2.5 py-[7px] cursor-pointer text-[11.5px]"
      style={{ ...borderFor(flashKeys, key), opacity: disabled ? 0.7 : 1, color: '#3c4453' }}
    >
      <span>{label}</span>
      <input
        type="checkbox"
        className="shrink-0"
        style={{ width: 15, height: 15, accentColor: '#145f5f' }}
        checked={!!config[key]}
        disabled={disabled}
        onChange={(e) => updateConfig(key, e.target.checked)}
      />
    </label>
  );

  const setupMissing = ['companyName', 'cin', 'fyEnded'].filter((k) => !String(config[k] || '').trim()).length;
  const signMissing = ['firmName', 'frn', 'partnerName', 'membershipNo', 'udin', 'place', 'reportDate']
    .filter((k) => !String(config[k] || '').trim()).length;

  return (
    <div className="space-y-2.5">
      <Section title="Report setup" missing={setupMissing}>
        <div>
          <label className={labelCls}>Company type</label>
          <div style={flashRing(flashKeys, 'companyType')}>
            <Select
              size="sm"
              value={config.companyType}
              onChange={(v) => updateConfig('companyType', v)}
              options={report.options.companyTypes}
            />
          </div>
        </div>
        {field('companyName', 'Company name', 'ABC Private Limited')}
        {field('cin', 'CIN', 'U00000XX0000PTC000000')}
        <div className="grid grid-cols-2 gap-2">
          {field('fyEnded', 'FY ended', '31st March, 2026')}
          <div>
            <label className={labelCls}>Framework</label>
            <div style={flashRing(flashKeys, 'framework')}>
              <Select
                size="sm"
                value={config.framework}
                onChange={(v) => updateConfig('framework', v)}
                options={report.options.frameworks}
              />
            </div>
          </div>
        </div>
      </Section>

      <Section title="CARO 2020 applicability" missing={caro.applies === null ? caro.missing.length : 0}>
        {/* The verdict, in the words the report will use. */}
        <div
          className="rounded-lg border px-2.5 py-2 text-[11.5px] leading-relaxed"
          style={{
            borderColor: caro.applies === true ? '#cfe0cb' : caro.applies === false ? '#ebdcb9' : '#e6cfcf',
            background: caro.applies === true ? '#f1f6f0' : caro.applies === false ? '#faf6eb' : '#fbf1f1',
            color: '#3c4453',
          }}
          data-testid="caro-verdict"
        >
          <div className="font-semibold mb-0.5" style={{ color: caro.applies === true ? '#4a6b3f' : caro.applies === false ? '#8a6a1f' : '#9a564a' }}>
            {caro.applies === true ? 'CARO 2020 applies — Annexure A will be produced'
              : caro.applies === false ? 'CARO 2020 not applicable — the report will say so; no Annexure A'
                : 'Not yet settled — Annexure A is included until it is'}
          </div>
          <div>{caro.reason}</div>
        </div>

        <div>
          <label className={labelCls}>Sector</label>
          <Select size="sm" value={config.caroSector || 'none'} onChange={(v) => updateConfig('caroSector', v)} options={report.options.caroSectors} />
        </div>

        {(config.companyType === 'pvt') && (
          <>
            <div>
              <label className={labelCls}>Holding or subsidiary of a public company?</label>
              <Select
                size="sm"
                value={config.caroHoldingOrSubsidiaryOfPublic === true ? 'yes' : config.caroHoldingOrSubsidiaryOfPublic === false ? 'no' : ''}
                onChange={(v) => updateConfig('caroHoldingOrSubsidiaryOfPublic', v === 'yes' ? true : v === 'no' ? false : null)}
                options={[{ value: '', label: 'Not answered' }, { value: 'no', label: 'No' }, { value: 'yes', label: 'Yes' }]}
              />
            </div>
            {field('caroPaidUpPlusReservesInr', 'Paid-up capital + reserves & surplus at balance-sheet date (₹)', 'e.g. 8500000')}
            {field('caroBorrowingsInr', 'Highest borrowings from banks / FIs during the year (₹)', 'e.g. 2500000')}
            {field('caroTotalRevenueInr', 'Total revenue for the year, Schedule III (₹)', 'e.g. 62000000')}
            <div className="text-[10.5px] text-[#7a7060] leading-relaxed">
              Private-company exclusion (para 1(2)(v)): all three within ₹1 cr / ₹1 cr / ₹10 cr, and not a holding or subsidiary of a public company. A small company under Sec 2(85) is exempt regardless.
            </div>
          </>
        )}

        <div>
          <label className={labelCls}>Auditor&apos;s decision</label>
          <Select size="sm" value={config.caroOverride || 'auto'} onChange={(v) => updateConfig('caroOverride', v)} options={report.options.caroOverrides} />
        </div>
      </Section>

      <Section title="Opinion & sections">
        <div>
          <label className={labelCls}>Audit opinion</label>
          <div style={flashRing(flashKeys, 'opinionType')}>
            <Select
              size="sm"
              value={config.opinionType}
              onChange={(v) => updateConfig('opinionType', v)}
              options={report.options.opinionTypes}
            />
          </div>
        </div>
        {config.opinionType === 'qualified' && report.options.qualificationBases && (
          <div>
            <label className={labelCls}>Qualification arises from</label>
            <div style={flashRing(flashKeys, 'qualificationBasis')}>
              <Select
                size="sm"
                value={config.qualificationBasis || 'misstatement'}
                onChange={(v) => updateConfig('qualificationBasis', v)}
                options={report.options.qualificationBases}
              />
            </div>
          </div>
        )}
        {toggle('includeKAM', 'Key Audit Matters (SA 701)', config.companyType === 'listed')}
        {toggle('includeOtherInfo', '"Other Information" (SA 720)')}
        {toggle('includeEmphasis', 'Emphasis of Matter (SA 706)')}
      </Section>

      <Section title="Auditor & signature" missing={signMissing} defaultOpen={false}>
        <div className="grid grid-cols-2 gap-2">
          {field('firmName', 'Firm name', 'XYZ & Associates')}
          {field('frn', 'FRN', '012345W')}
        </div>
        {field('partnerName', 'Engagement partner', 'CA Ramesh Kumar')}
        <div className="grid grid-cols-2 gap-2">
          {field('membershipNo', 'Membership no.', '123456')}
          {field('udin', 'UDIN', '26123456BK…')}
        </div>
        <div className="grid grid-cols-2 gap-2">
          {field('place', 'Place', 'Mumbai')}
          {field('reportDate', 'Date of report', '15th May, 2026')}
        </div>
      </Section>

      <CaroClauseOverrides config={config} updateConfig={updateConfig} />

      {/* Statutory applicability — read-only status matrix for the chosen type */}
      <div className="rounded-xl border bg-white overflow-hidden" style={{ borderColor: '#e2d9c8' }}>
        <div className="flex items-center justify-between gap-2 px-3 py-2">
          <span className="text-[11px] font-bold uppercase tracking-[0.5px] text-[#08111f]">Applicability</span>
          <span className="text-[10px] font-semibold truncate" style={{ color: '#9a9080' }}>{matrix.name}</span>
        </div>
        <div className="px-3 pb-2">
          {matrix.rows.map((r) => (
            <div key={r.label} className="flex items-center justify-between gap-2 py-[4px]">
              <span className="text-[11px]" style={{ color: '#3c4453' }}>{r.label}</span>
              <span className="text-[9.5px] font-bold px-2 py-[2px] rounded-full whitespace-nowrap" style={APPLIC_PILL[r.state] || APPLIC_PILL.no}>
                {r.value}
              </span>
            </div>
          ))}
        </div>
        {matrix.note && (
          <div className="px-3 py-2 text-[10px] leading-[1.5] border-t" style={{ color: '#7a7060', borderColor: '#f0ece4', background: '#fcfbf7' }}>
            {matrix.note}
          </div>
        )}
      </div>
    </div>
  );
}
